import { isEmpty } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";

import {
  useDeleteSalesUnit,
  useDeleteUploadProductImage,
  useGetProductById,
  usePostUSalesUnit,
  usePutEditProduct,
  usePutEditSalesUnit,
  useUploadProductImage
} from "../../../api/product";
import { CommonModal } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON, UNIT_TYPES } from "../../../config/constants";
import { IsRootProduct } from "../../../config/enums/ProductType.enum";
import { errorView, successMessage } from "../../../helpers/ErrorHandler";
import ProductMatching from "../component/productMatching/ProductMatching";
import ProductInfoForm from "./component/ProductInfoForm";
import ProductUnitForm from "./component/ProductUnitForm";
import { ProductDetails, ProductEditModalProps, SalesUnit } from "./types/productTypes";

import "./productEdit.scss";

const ProductEditModal: React.FC<ProductEditModalProps> = ({
  visibleModal,
  setVisibleModal,
  productId
}) => {
  const [addProductUnit, setProductUnit] = useState<SalesUnit[]>([]);
  const [productType, setProductType] = useState<string>("PROCESS");
  const [enableSizeEditStatus, setEnableSizeEditStatus] = useState<boolean>(false);
  const [enableSizeEdit, setEnableSizeEdit] = useState<SalesUnit | null>(null);
  const [defaultType, setDefaultType] = useState<string>(UNIT_TYPES[0].value);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<SalesUnit | null>(null);
  const [isRootProduct, setIsRootProduct] = useState<string>(IsRootProduct.NO);
  const [visibleAverageConfirmModal, setVisibleAverageConfirmModal] = useState<boolean>(false);
  const [pendingAverageValue, setPendingAverageValue] = useState<string | null>(null);
  const isRootProductYes = isRootProduct === IsRootProduct.YES;
  const statusCloseModal = useRef(true);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setValue: setProductValue
  } = useForm<ProductDetails>({
    mode: "all"
  });

  const {
    register: registerUnitSize,
    control: controlUnitSize,
    formState: { errors: errorsUnit },
    handleSubmit: handleSubmitUnitSize,
    reset: resetUnitSize,
    setValue
  } = useForm<SalesUnit>();

  const {
    data: productDetails,
    isLoading: productDetailsLoading,
    refetch
  } = useGetProductById(productId);

  const { isLoading: editProductLoading, mutate: editProductData } = usePutEditProduct();
  const { isLoading: editProductUnitLoading, mutate: editSalesUnit } = usePutEditSalesUnit();
  const { isLoading: addProductUnitLoading, mutate: addNewSalesUnit } = usePostUSalesUnit();
  const { isLoading: isLoadingProductImage, mutate: uploadProductImage } = useUploadProductImage();
  const { isLoading: isLoadingProductImageDelete, mutate: deleteProductImage } =
    useDeleteUploadProductImage();
  const { mutate: deleteSalesUnit, isLoading: isLoadingDeleteProductSalesUnit } =
    useDeleteSalesUnit();

  const setUnitSizeUpdate = (data: SalesUnit) => {
    setEnableSizeEdit(data);
    setEnableSizeEditStatus(true);
    setValue("sales_unit_name", data.sales_unit_name);
    setValue("sales_unit_size", data.sales_unit_size);
    setValue("sales_unit_type", data.sales_unit_type);
    setValue("ean_barcode", data.ean_barcode);
  };

  const updateUnitType = (value: string) => {
    setDefaultType(value);
    setValue("sales_unit_type", value);
  };

  const addUnitData = (data: any) => {
    let salesUnit = {
      product_id: productDetails?._id,
      sales_units: [
        {
          sales_unit_size: data?.sales_unit_size ?? "",
          sales_unit_type: defaultType ?? "",
          ean_barcode: data?.ean_barcode ?? "",
          sales_unit_name: data?.sales_unit_name?.trim() ?? ""
        }
      ]
    };
    addNewSalesUnit(salesUnit, {
      onSuccess: () => {
        successMessage("Sales unit data added successfully");
        refetch(productId);
        setDefaultType(UNIT_TYPES[0].value);
        reset({
          sales_unit_size: "",
          sales_unit_type: "",
          ean_barcode: "",
          sales_unit_name: ""
        });
        resetUnitSize();
      }
    });
  };

  const handleSalesUnitDeleteConfirmation = () => {
    if (deleteData?.has_drawing) {
      errorView("This product already has a digital twin (Mapping), it cannot be deleted.");
      return;
    }

    deleteUnitData(deleteData);
  };

  const deleteUnitData = (data: any) => {
    deleteSalesUnit(
      { productId: productId, salesUnitId: data?._id },
      {
        onSuccess: () => {
          refetch(productId);
          setDeleteData(null);
          setVisibleDeleteModal(false);
        }
      }
    );
  };

  const editUnitSize = (data: any) => {
    const updateValue = {
      product_id: productDetails?._id,
      sales_unit_id: enableSizeEdit?._id,
      sales_unit_name: data.sales_unit_name.trim(),
      sales_unit_size: data.sales_unit_size,
      sales_unit_type: data.sales_unit_type,
      ean_barcode: data.ean_barcode
    };
    editSalesUnit(updateValue, {
      onSuccess: () => {
        resetUnitSize();
        setDefaultType(UNIT_TYPES[0].value);
        refetch();
      }
    });
  };

  const initiateProduct = () => {
    if (productDetails) {
      setProductValue("product_name", productDetails.product_name);
      setProductValue("registration_number", productDetails.registration_number ?? "");
      setProductValue("product_other_info", productDetails.product_other_info ?? "");
      setProductUnit(productDetails.sales_units ?? []);
      setProductType(productDetails.product_type);
      const rootProductValue = productDetails.is_average_product
        ? IsRootProduct.YES
        : IsRootProduct.NO;
      setIsRootProduct(rootProductValue);
    }
  };

  useEffect(() => {
    initiateProduct();
    return () => {
      setEnableSizeEditStatus(false);
      setProductUnit([]);
      setProductType("PROCESS");
    };
  }, [productDetails, visibleModal]);

  const enableUnitSizeAdd = useMemo(() => {
    return (
      (isRootProduct === IsRootProduct.YES && addProductUnit.length === 0) ||
      isRootProduct === IsRootProduct.NO
    );
  }, [isRootProduct, addProductUnit]);

  const checkProductSaveCondition = (data: any) => {
    if (isEmpty(data.product_name.trim())) {
      errorView("Product name is required");
      return true;
    } else if (addProductUnit.length <= 0) {
      errorView("Please add product sizes");
      return true;
    } else {
      return false;
    }
  };
  const isRootProductBool = useMemo(() => isRootProduct === IsRootProduct.YES, [isRootProduct]);

  const saveAndNavigate = () => {
    if (!statusCloseModal.current) return;
    reset();
    resetUnitSize();
  };

  const handleAverageProduct = async (data: any) => {
    // Show confirmation modal and store pending value
    setPendingAverageValue(data);
    setVisibleAverageConfirmModal(true);
  };

  const confirmAverageProductChange = () => {
    if (!pendingAverageValue) return;

    // Update local state
    if (pendingAverageValue) {
      statusCloseModal.current = false;
      setIsRootProduct(pendingAverageValue);
    }

    // Close modal and reset
    setVisibleAverageConfirmModal(false);
    setPendingAverageValue(null);
  };

  const cancelAverageProductChange = () => {
    setVisibleAverageConfirmModal(false);
    setPendingAverageValue(null);
  };

  useEffect(() => {
    if (isRootProduct && !statusCloseModal.current) {
      (async () => {
        await handleSubmit(onSubmit)();
      })();
    }
  }, [isRootProduct, statusCloseModal.current]);

  const cancelProduct = () => {
    setVisibleModal(true);
    reset();
    resetUnitSize();
  };

  const saveProduct = () => {
    const values = getValues();
    onSubmit(values);
  };

  const onSubmit = (data: any) => {
    if (!checkProductSaveCondition(data)) {
      let { product_name, product_other_info, registration_number } = data;
      let productData = {
        _id: productDetails?._id,
        product_name: product_name.trim(),
        product_other_info: product_other_info?.trim() ?? "",
        registration_number,
        product_type: productType,
        is_average_product: isRootProductBool
      };
      editProductData(productData, {
        onSuccess: () => {
          saveAndNavigate();
          setVisibleModal(true);
        }
      });
      if (statusCloseModal.current) {
        setVisibleModal(true);
        initiateProduct();
      }
    }
  };

  if (
    productDetailsLoading ||
    editProductLoading ||
    editProductUnitLoading ||
    addProductUnitLoading ||
    isLoadingProductImage ||
    isLoadingProductImageDelete ||
    isLoadingDeleteProductSalesUnit
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => cancelProduct()}
        centered
        title="Edit Product"
      >
        <Grid>
          <Grid.Column width={16}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CommonSplitContainer
                topChild={
                  <>
                    <ProductInfoForm
                      register={register}
                      errors={errors}
                      productDetails={productDetails}
                      productType={productType}
                      setProductType={setProductType}
                      isRootProduct={isRootProduct}
                      handleAverageProduct={handleAverageProduct}
                      uploadProductImage={uploadProductImage}
                      deleteProductImage={deleteProductImage}
                      refetch={refetch}
                    />
                    <ProductUnitForm
                      addProductUnit={addProductUnit}
                      defaultType={defaultType}
                      setDefaultType={updateUnitType}
                      enableSizeEditStatus={enableSizeEditStatus}
                      enableSizeEdit={enableSizeEdit}
                      setUnitSizeUpdate={setUnitSizeUpdate}
                      editUnitSize={editUnitSize}
                      addUnitData={addUnitData}
                      setDeleteData={setDeleteData}
                      setVisibleDeleteModal={setVisibleDeleteModal}
                      registerUnitSize={registerUnitSize}
                      controlUnitSize={controlUnitSize}
                      errorsUnit={errorsUnit}
                      handleSubmitUnitSize={handleSubmitUnitSize}
                      enableUnitSizeAdd={enableUnitSizeAdd}
                    />
                    {isRootProductYes && productDetails?._id && !DISABLE_IS_ROOT_PRODUCT_BUTTON && (
                      <ProductMatching id={productDetails._id} />
                    )}
                  </>
                }
                bottomChild={
                  <MainBottomMultipleButtonView
                    leftButtons={[
                      {
                        title: "Cancel",
                        onClick: () => cancelProduct(),
                        buttonOutLine: true,
                        buttonView: true
                      }
                    ]}
                    rightButtons={[
                      {
                        contentView: 16,
                        title: isRootProductYes ? "Save and Exit" : "Save",
                        onClick: () => handleSubmit(saveProduct)(),
                        buttonView: true
                      }
                    ]}
                  />
                }
              />
            </form>
          </Grid.Column>
        </Grid>
      </CommonModal>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteData(null);
        }}
        approve={() => {
          setVisibleDeleteModal(false);
          handleSalesUnitDeleteConfirmation();
        }}
        title="Delete Unit Size"
        subTitle="Are you sure you want to delete unit size?"
      />
      <ConfirmModal
        viewModal={visibleAverageConfirmModal}
        closeModal={() => cancelAverageProductChange()}
        cancel={() => cancelAverageProductChange()}
        approve={() => confirmAverageProductChange()}
        title="Change Average Impact Product Status"
        subTitle={
          pendingAverageValue === IsRootProduct.YES
            ? "Are you sure you want to enable Average Impact Product? This will link sales units for average calculations."
            : "Are you sure you want to disable Average Impact Product? This will unlink all connected sales units."
        }
      />
    </>
  );
};

export default ProductEditModal;
